import * as actionTypes from "../constants/actionTypes";
import * as methods from "../constants/methods";
import * as handlers from "../constants/handlers";
import * as config from "../constants/config";

/**
 * @param   {Number}        tabId
 * @param   {Boolean}       local
 * @param   {Boolean}       fts
 * @param   {Boolean}       tree
 * @param   {Boolean}       compOnly
 * @param   {String | null} handler
 * @returns {Object}
 */
export const getProducts = (tabId, local, fts, tree, compOnly = true, handler = handlers.GET_PRODUCTS_BY_PARAMS) => ({
    type: actionTypes.SERVER_REQUEST,
    payload: {
        type: methods.METHOD_SEARCH_PRODUCTS,
        params: {
            isLocal: local,
            fts: fts,
            tree: tree,
            compatibleOnly: compOnly
        }
    },
    method: handler,
    tabId: tabId
});

/**
 * @param   {Number}        tabId
 * @param   {Number | null} fandeckId
 * @param   {Number | null} colorId
 * @param   {String | null} baseName
 * @param   {String | null} searchName
 * @param   {Boolean}       local
 * @param   {Boolean}       fts
 * @param   {Boolean}       tree
 * @param   {Boolean}       compOnly
 * @param   {String | null} handler
 * @returns {Object}
 */
export const getProductsByParams = (
    tabId,
    fandeckId,
    colorId,
    baseName,
    searchName,
    local,
    fts,
    tree = true,
    compOnly = true,
    handler = handlers.GET_PRODUCTS_BY_PARAMS,
    next = {}
) => ({
    type: actionTypes.SERVER_REQUEST,
    payload: {
        type: methods.METHOD_SEARCH_PRODUCTS,
        params: {
            fandeckId: fandeckId,
            colorId: colorId,
            baseName: baseName,
            searchName: searchName !== null ? (searchName.trim() === "" ? null : searchName) : null,
            isLocal: local,
            fts: fts,
            tree: tree,
            compatibleOnly: compOnly
        }
    },
    method: handler,
    tabId: tabId,
    next: next
});

/**
 * @param   {Number}        tabId
 * @param   {Boolean}       local
 * @param   {Boolean}       fts
 * @param   {Boolean}       tree
 * @param   {Boolean}       compOnly
 * @param   {String | null} handler
 * @param   {Number | null} rowCount
 * @param   {Number | null} offset
 * @param   {Boolean}       reflData
 * @returns {Object}
 */
export const getFandecks = (
    tabId,
    local,
    fts,
    tree,
    compOnly = true,
    handler = handlers.GET_FANDECKS_BY_PARAMS,
    rowCount = config.MAX_FANDECK_LIMIT,
    offset = 0,
    reflData = false
) => ({
    type: actionTypes.SERVER_REQUEST,
    payload: {
        type: methods.METHOD_SEARCH_FANDECKS,
        params: {
            isLocal: local,
            fts: fts,
            tree: tree,
            compatibleOnly: compOnly,
            rowCount: rowCount,
            offset: offset,
            reflData: reflData
        }
    },
    method: handler,
    tabId: tabId
});

/**
 * @param   {Number}        tabId
 * @param   {Number | null} productId
 * @param   {Number | null} colorId
 * @param   {String | null} baseName
 * @param   {String | null} searchName
 * @param   {Boolean}       local
 * @param   {Boolean}       fts
 * @param   {Boolean}       tree
 * @param   {Boolean}       compOnly
 * @param   {String | null} handler
 * @param   {Number | null} rowCount
 * @param   {Number | null} offset
 * @param   {Boolean}       reflData
 * @param   {Object | null} next
 * @returns {Object}
 */
export const getFandecksByParams = (
    tabId,
    productId,
    colorId,
    baseName,
    searchName,
    local,
    fts,
    tree,
    compOnly = true,
    handler = handlers.GET_FANDECKS_BY_PARAMS,
    rowCount = config.MAX_FANDECK_LIMIT,
    offset = 0,
    reflData = false,
    next = null
) => ({
    type: actionTypes.SERVER_REQUEST,
    payload: {
        type: methods.METHOD_SEARCH_FANDECKS,
        params: {
            productId: productId,
            colorId: colorId,
            baseName: baseName,
            searchName: searchName !== null ? (searchName.trim() === "" ? null : searchName) : null,
            isLocal: local,
            fts: fts,
            tree: tree,
            compatibleOnly: compOnly,
            rowCount: rowCount,
            offset: offset,
            reflData: reflData
        }
    },
    method: handler,
    tabId: tabId,
    next: next
});

/**
 * @param   {Number}        tabId
 * @param   {Boolean}       local
 * @param   {Boolean}       fts
 * @param   {Boolean}       levenshtein
 * @param   {Boolean}       compOnly
 * @param   {String | null} handler
 * @param   {Number | null} rowCount
 * @param   {Number | null} offset
 * @param   {String | null} orderBy
 * @param   {Boolean}       asc
 * @param   {Boolean}       reflData
 * @returns {Object}
 */
export const getColors = (
    tabId,
    local,
    fts,
    levenshtein,
    compOnly = true,
    handler = handlers.GET_COLORS_BY_PARAMS,
    rowCount = config.MAX_FORMULA_LIMIT,
    offset = 0,
    orderBy = null,
    asc = true,
    reflData = false
) => ({
    type: actionTypes.SERVER_REQUEST,
    payload: {
        type: methods.METHOD_SEARCH_COLORS,
        params: {
            isLocal: local,
            fts: fts,
            compatibleOnly: compOnly,
            rowCount: rowCount,
            offset: offset,
            orderBy: orderBy,
            asc: asc,
            reflData: reflData,
            sortLevenshtein: levenshtein
        }
    },
    method: handler,
    tabId: tabId
});

/**
 * @param   {Number}        tabId
 * @param   {Number | null} productId
 * @param   {Number | null} fandeckId
 * @param   {String | null} baseName
 * @param   {String | null} colorName
 * @param   {String | null} colorCode
 * @param   {Boolean}       local
 * @param   {Boolean}       fts
 * @param   {Boolean}       levenshtein
 * @param   {Boolean}       compOnly
 * @param   {String | null} handler
 * @param   {Number | null} rowCount
 * @param   {Number | null} offset
 * @param   {String | null} orderBy
 * @param   {Boolean}       asc
 * @param   {Boolean}       reflData
 * @param   {Object | null} next
 * @returns {Object}
 */
export const getColorsByParams = (
    tabId,
    productId,
    fandeckId,
    baseName,
    colorName,
    colorCode,
    local,
    fts,
    levenshtein,
    compOnly = true,
    handler = handlers.GET_COLORS_BY_PARAMS,
    rowCount = config.MAX_FORMULA_LIMIT,
    offset = 0,
    orderBy = null,
    asc = true,
    reflData = false,
    next = null
) => ({
    type: actionTypes.SERVER_REQUEST,
    payload: {
        type: methods.METHOD_SEARCH_COLORS,
        params: {
            productId: productId,
            fandeckId: fandeckId,
            searchName: colorName !== null ? (colorName.trim() === "" ? null : colorName) : null,
            searchCode: colorCode !== null ? (colorCode.trim() === "" ? null : colorCode) : null,
            baseName: baseName,
            isLocal: local,
            fts: fts,
            sortLevenshtein: levenshtein,
            compatibleOnly: compOnly,
            rowCount: rowCount,
            offset: offset,
            orderBy: orderBy,
            asc: asc,
            reflData: reflData
        }
    },
    method: handler,
    tabId: tabId,
    next: next
});

/**
 * @param   {Number}        tabId
 * @param   {String}        orderBy
 * @param   {Boolean}       compOnly
 * @param   {Number | null} rowCount
 * @param   {Boolean}       cifImage
 * @returns {Object}
 */
export const browseColors = (tabId, orderBy, compOnly, rowCount, cifImage) => ({
    type: actionTypes.SERVER_REQUEST,
    payload: {
        type: methods.METHOD_BROWSE_CIF,
        params: {
            orderBy: orderBy,
            compOnly: compOnly,
            rowCount: rowCount,
            offset: 0,
            cifImage: cifImage,
            reflData: true
        }
    },
    method: handlers.BROWSE_CIF,
    tabId: tabId
});

/**
 * @param   {Number}        tabId
 * @param   {Number | null} productId
 * @param   {Number | null} fandeckId
 * @param   {String}        orderBy
 * @param   {Boolean}       compOnly
 * @param   {Boolean}       cifImage
 * @param   {Number | null} rowCount
 * @param   {Number | null} offset
 * @param   {String | null} handler
 * @returns {Object}
 */
export const browseColorsByParams = (
    tabId,
    productId,
    fandeckId,
    orderBy,
    compOnly,
    cifImage,
    rowCount = config.MAX_FORMULA_LIMIT,
    offset = 0,
    handler = handlers.BROWSE_CIF
) => ({
    type: actionTypes.SERVER_REQUEST,
    payload: {
        type: methods.METHOD_BROWSE_CIF,
        params: {
            productId: productId,
            fandeckId: fandeckId,
            orderBy: orderBy,
            compOnly: compOnly,
            rowCount: rowCount,
            offset: offset,
            cifImage: cifImage,
            reflData: true
        }
    },
    method: handler,
    tabId: tabId
});

/**
 * @param   {Number}        tabId
 * @param   {Boolean}       local
 * @param   {Boolean}       fts
 * @param   {String | null} handler
 * @returns {Object}
 */
export const getBases = (tabId, local, fts, handler = handlers.GET_BASES_BY_PARAMS) => ({
    type: actionTypes.SERVER_REQUEST,
    payload: {
        type: methods.METHOD_SEARCH_BASES,
        params: {
            isLocal: local,
            fts: fts,
            rowCount: config.MAX_BASE_LIMIT
        }
    },
    method: handler,
    tabId: tabId
});

/**
 * @param   {Number}        tabId
 * @param   {Number | null} productId
 * @param   {Number | null} fandeckId
 * @param   {Number | null} colorId
 * @param   {String | null} searchName
 * @param   {Boolean}       local
 * @param   {Boolean}       fts
 * @param   {String | null} handler
 * @param   {Number | null} rowCount
 * @param   {Number | null} offset
 * @returns {Object}
 */
export const getBasesByParams = (
    tabId,
    productId,
    fandeckId,
    colorId,
    searchName,
    local,
    fts,
    handler = handlers.GET_BASES_BY_PARAMS,
    rowCount = config.MAX_BASE_LIMIT,
    offset = 0
) => ({
    type: actionTypes.SERVER_REQUEST,
    payload: {
        type: methods.METHOD_SEARCH_BASES,
        params: {
            productId: productId,
            fandeckId: fandeckId,
            colorId: colorId,
            searchName: searchName !== null ? (searchName.trim() === "" ? null : searchName) : null,
            isLocal: local,
            fts: fts,
            rowCount: rowCount,
            offset: offset
        }
    },
    method: handler,
    tabId: tabId
});

/**
 * @param   {Number}        tabId
 * @param   {Boolean}       local
 * @param   {Boolean}       fts
 * @param   {String | null} handler
 * @returns {Object}
 */
export const getBipps = (tabId, local, fts, handler = handlers.GET_BIPP_BY_PARAMS) => ({
    type: actionTypes.SERVER_REQUEST,
    payload: {
        type: methods.METHOD_SEARCH_BIPP,
        params: {
            isLocal: local,
            fts: fts,
            rowCount: config.MAX_BIPP_LIMIT
        }
    },
    method: handler,
    tabId: tabId
});

/**
 * @param   {Number}        tabId
 * @param   {Number | null} productId
 * @param   {Number | null} fandeckId
 * @param   {Number | null} colorId
 * @param   {String | null} baseName
 * @param   {String | null} searchName
 * @param   {Boolean}       local
 * @param   {Boolean}       fts
 * @param   {String | null} handler
 * @param   {Number | null} rowCount
 * @param   {Number | null} offset
 * @returns {Object}
 */
export const getBippsByParams = (
    tabId,
    productId,
    fandeckId,
    colorId,
    baseName,
    searchName,
    local,
    fts,
    handler = handlers.GET_BIPP_BY_PARAMS,
    rowCount = config.MAX_BIPP_LIMIT,
    offset = 0
) => ({
    type: actionTypes.SERVER_REQUEST,
    payload: {
        type: methods.METHOD_SEARCH_BIPP,
        params: {
            productId: productId,
            fandeckId: fandeckId,
            colorId: colorId,
            baseName: baseName,
            searchName: searchName !== null ? (searchName.trim() === "" ? null : searchName) : null,
            isLocal: local,
            fts: fts,
            rowCount: rowCount,
            offset: offset
        }
    },
    method: handler,
    tabId: tabId
});
